import { useEffect } from "react";

type HotmartIframeFixOptions = {
  /** Minimum height to avoid internal iframe scrollbars */
  minHeightDesktop?: number;
  minHeightMobile?: number;
  /** Cap height so it doesn't create a giant empty grey area */
  maxHeightDesktop?: number;
  maxHeightMobile?: number;
  /** Portion of viewport height to use (0-1). Keeps it responsive. */
  viewportFillDesktop?: number;
  viewportFillMobile?: number;
};

const isMobileViewport = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

/**
 * Hotmart's sales funnel widget mounts an iframe with a fixed height.
 * When that height is smaller than its content, it shows an inner scrollbar (grey bar).
 * We can't style inside a cross-origin iframe, so the practical fix is to force a larger iframe height.
 */
export function useHotmartIframeFix(
  containerId: string,
  opts: HotmartIframeFixOptions = {}
) {
  const {
    // Defaults tuned to avoid huge empty areas while still preventing inner iframe scrollbars.
    minHeightDesktop = 520,
    minHeightMobile = 680,
    maxHeightDesktop = 720,
    maxHeightMobile = 900,
    viewportFillDesktop = 0.6,
    viewportFillMobile = 0.78,
  } = opts;

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const apply = () => {
      const iframe = container.querySelector("iframe") as HTMLIFrameElement | null;
      if (!iframe) return;
      const minHeight = isMobileViewport() ? minHeightMobile : minHeightDesktop;
      const maxHeight = isMobileViewport() ? maxHeightMobile : maxHeightDesktop;
      const viewportFill = isMobileViewport() ? viewportFillMobile : viewportFillDesktop;

      const viewportTarget = Math.round(window.innerHeight * viewportFill);
      const height = Math.max(minHeight, Math.min(viewportTarget, maxHeight));

      // Container
      container.style.width = "100%";
      container.style.overflow = "hidden";
      container.style.minHeight = `${height}px`;

      // Iframe
      iframe.setAttribute("scrolling", "no");
      iframe.style.display = "block";
      iframe.style.width = "100%";
      iframe.style.maxWidth = "100%";
      iframe.style.border = "0";
      iframe.style.overflow = "hidden";
      iframe.style.background = "transparent";
      iframe.style.minHeight = `${height}px`;
      iframe.style.height = `${height}px`;
    };

    // First try immediately
    apply();

    // Watch for the widget to mount/replace the iframe
    const observer = new MutationObserver(() => apply());
    observer.observe(container, { childList: true, subtree: true });

    // Also re-apply on resize/orientation changes
    const onResize = () => apply();
    window.addEventListener("resize", onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [
    containerId,
    minHeightDesktop,
    minHeightMobile,
    maxHeightDesktop,
    maxHeightMobile,
    viewportFillDesktop,
    viewportFillMobile,
  ]);
}
